import { Card, DimensionId, StepDef, Theme } from './types';

export const dimOrder: DimensionId[] = [
  'ZUSAMMENARBEIT',
  'HANDELN',
  'SEIN',
  'DENKEN',
  'BEZIEHUNG',
];

export const THEMES: Record<DimensionId, Theme> = {
  ZUSAMMENARBEIT: {
    name: 'ZUSAMMENARBEIT',
    color: '#928cbb',
    accent: '#6f68a0',
    ink: '#1c1830',
    inkSoft: 'rgba(0,0,0,.5)',
    angle: 36,
  },
  HANDELN: {
    name: 'HANDELN',
    color: '#e4a8c0',
    accent: '#c97f9d',
    ink: '#3a1626',
    inkSoft: 'rgba(0,0,0,.5)',
    angle: 108,
  },
  SEIN: {
    name: 'SEIN',
    color: '#71c89b',
    accent: '#4ba577',
    ink: '#14241c',
    inkSoft: 'rgba(0,0,0,.5)',
    angle: 180,
  },
  DENKEN: {
    name: 'DENKEN',
    color: '#a6e4fd',
    accent: '#6dc3e8',
    ink: '#0e2a38',
    inkSoft: 'rgba(0,0,0,.5)',
    angle: 252,
  },
  BEZIEHUNG: {
    name: 'BEZIEHUNG',
    color: '#fddbbf',
    accent: '#e8b48a',
    ink: '#3a2510',
    inkSoft: 'rgba(0,0,0,.5)',
    angle: 324,
  },
};

export const theme = (id: DimensionId | string | undefined): Theme =>
  THEMES[(id as DimensionId) ?? 'ZUSAMMENARBEIT'] ?? THEMES.ZUSAMMENARBEIT;

export interface QuestionDef {
  text: string;
  urlVideo?: string;
}

export const QUESTIONS: Record<DimensionId, QuestionDef[]> = {
  ZUSAMMENARBEIT: [
    {
      text: 'In unserem Team drückt sich jeder klar und verständlich aus. Jede Person darf ungestört ausreden, und das Gesagte wird nicht sofort bewertet oder kommentiert.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Zusammenarbeit-1.mp4',
    },
    {
      text: 'In unserem Team gelingt es, interne wie externe Kund:innen sehr früh zu integrieren und mitzugestalten zu lassen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Zusammenarbeit-2.mp4',
    },
    {
      text: 'In unserem Team wertschätzen wir die Vielfalt der Menschen und setzen uns für Chancengleichheit ein.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Zusammenarbeit-3.mp4',
    },
    {
      text: 'Wir können uns aufeinander verlassen, und unsere Handlungen fördern Vertrauen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Zusammenarbeit-4.mp4',
    },
    {
      text: 'Unser Team organisiert sich rasch und ist ein Motor für Veränderung, sowohl innerhalb des Unternehmens als auch extern.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Zusammenarbeit-5.mp4',
    },
  ],
  HANDELN: [
    {
      text: 'Unser Team ist in der Lage, mutige und weitreichende Ziele und Gedanken zu formulieren.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Handeln-1.mp4',
    },
    {
      text: 'Unser Team setzt die vorhandenen Ressourcen stets bedacht ein, sodass wir langfristig erfolgreich agieren.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Handeln-2.mp4',
    },
    {
      text: 'Unser Team sucht nach Lösungen, statt Probleme zu betonen. Dabei finden wir originelle Ideen und durchbrechen Konventionen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Handeln-3.mp4',
    },
    {
      text: 'Unser Team sieht eine erfolgreiche Zukunft und spricht mit Begeisterung über künftige Herausforderungen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Handeln-4.mp4',
    },
    {
      text: 'Unser Team bringt begonnene Projekte konsequent zu Ende und macht auch bei Rückschlägen weiter.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Handeln-5.mp4',
    },
  ],
  SEIN: [
    {
      text: 'Unser Team verfolgt konsequent ein gemeinsames Ziel und lässt sich davon nicht abbringen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Sein-1.mp4',
    },
    {
      text: 'Auf Zusagen und Versprechen unseres Teams ist Verlass. Wir leben verbindlich nach gemeinsamen Werten und stehen dafür ein.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Sein-2.mp4',
    },
    {
      text: 'Unser Team reflektiert regelmäßig sein Handeln und sucht beständig nach Chancen der Weiterentwicklung.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Sein-3.mp4',
    },
    {
      text: 'Wir kennen die Stärken und Schwächen der Teammitglieder und unsere Wirkung auf andere Einheiten.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Sein-4.mp4',
    },
    {
      text: 'Unser Team setzt klare Prioritäten und bearbeitet diese mit voller Aufmerksamkeit und Energie.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Sein-5.mp4',
    },
  ],
  DENKEN: [
    {
      text: 'In unserem Team fördern wir kontroverse Stimmen und beurteilen unsere Situation und Aufgaben kritisch.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Denken-1.mp4',
    },
    {
      text: 'Unser Team ist in der Lage, komplexe Sachverhalte zu erfassen und geeignete Vorgehensweisen zu entwickeln.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Denken-2.mp4',
    },
    {
      text: 'Unserem Team fällt es leicht, andere Perspektiven einzunehmen, eingefahrene Überzeugungen zu hinterfragen und zu überwinden.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Denken-3.mp4',
    },
    {
      text: 'Wir ziehen Energie und Motivation aus unserem gemeinsamen Auftrag.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Denken-4.mp4',
    },
    {
      text: 'Wir denken weit voraus.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Denken-5.mp4',
    },
  ],
  BEZIEHUNG: [
    {
      text: 'Wir begegnen einander mit Wertschätzung und Respekt und anerkennen die Leistung aller im Team.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Beziehung-1.mp4',
    },
    {
      text: 'Es ist unserem Team wichtig, ein starkes Netzwerk innerhalb und außerhalb des Unternehmens zu pflegen und auszubauen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Beziehung-2.mp4',
    },
    {
      text: 'Unser Team setzt in entscheidenden Momenten kollektive Bedürfnisse vor persönliche Interessen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Beziehung-3.mp4',
    },
    {
      text: 'In unserem Team wird kritisches Feedback dankbar angenommen.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Beziehung-4.mp4',
    },
    {
      text: 'In unserem Team ist es normal, einander zu helfen und sich auch für den anderen zu engagieren.',
      urlVideo:
        'https://victor-bucket.fra1.cdn.digitaloceanspaces.com/Insights-Beziehung-5.mp4',
    },
  ],
};

export const findQuestionVideo = (q: string): string | undefined => {
  for (let i = 0; i < dimOrder.length; i += 1) {
    const found = QUESTIONS[dimOrder[i]].find(item => item.text === q);
    if (found?.urlVideo) return found.urlVideo;
  }
  return undefined;
};

export const defaultCoreValues: string[] = ['Wachstum', 'Freiheit', 'Freude'];

export const stepDefs: StepDef[] = [
  { n: 1, label: 'Dreht das IDG-Rad' },
  {
    n: 2,
    label: 'Diskutiert die Frage im Team',
    hint: 'Hefte mindestens eine Notiz an, um diesen Schritt abzuschließen.',
  },
  { n: 3, label: 'Ordnet eure Kernwerte zu' },
  { n: 4, label: 'Bewertet mit Sternen: Lebt ihr das im Unternehmen?' },
  { n: 5, label: 'Optional: To-Dos ableiten', opt: true },
];

export const EB_LOGO_URL =
  'https://www.emotion-banking.at/wp-content/uploads/2026/05/eb_Icon_Kreis_trans-1.png';

let nextCardId = 100;
let nextNoteId = 500;
let zCounter = 100;

export const syncIdCounters = (cards: Card[]): void => {
  cards.forEach(c => {
    if (c.id >= nextCardId) nextCardId = c.id + 1;
    c.notes.forEach(n => {
      if (n.id >= nextNoteId) nextNoteId = n.id + 1;
      if (n.z >= zCounter) zCounter = n.z + 1;
    });
  });
};

export const nextCardIdRef = (): number => {
  const id = nextCardId;
  nextCardId += 1;
  return id;
};

export const nextNoteIdRef = (): number => {
  const id = nextNoteId;
  nextNoteId += 1;
  return id;
};

export const nextZIndexRef = (): number => {
  zCounter += 1;
  return zCounter;
};
